import { apiService } from "../services/api";
import type { AppDispatch } from "../state/store";
import {
  showAlertDialog,
  showConfirmDialog,
} from "../state/utils/confirmationDialogUtils";

export const PURCHASED_ITEMS_INFO_TITLE = "Purchased Items on Job Card";
export const PURCHASED_OVERLAP_CONFIRM_TITLE = "Duplicate Purchase Warning";

export type PurchasedOverlapItem = {
  codeid: string;
  item_name: string;
  purchased_qty: number;
  purchase_no: string;
  purchase_date: string;
  source: string;
};

export type PurchasedOverlapValidation = {
  has_overlap: boolean;
  service_details_id?: string;
  jobcard_no?: string;
  items: PurchasedOverlapItem[];
  message?: string;
};

export const isPartsRequestContext = (
  screenId?: string | null,
  moduleEndpoint?: string | null
): boolean => {
  const sid = String(screenId || "").trim();
  const module = String(moduleEndpoint || "").trim();
  return module === "parts_request" || sid === "160" || sid.includes("parts_request");
};

export const isPartsIssueContext = (
  screenId?: string | null,
  moduleEndpoint?: string | null
): boolean => {
  const sid = String(screenId || "").trim();
  const module = String(moduleEndpoint || "").trim();
  return module === "parts_issue" || sid === "120" || sid.includes("parts_issue");
};

export const isPartsRequestOrIssueContext = (
  screenId?: string | null,
  moduleEndpoint?: string | null
): boolean =>
  isPartsRequestContext(screenId, moduleEndpoint) ||
  isPartsIssueContext(screenId, moduleEndpoint);

const toNumber = (value: unknown): number => {
  const n = parseFloat(String(value ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const collectCodeidsFromRows = (
  rows: unknown[],
  options?: { issueOnly?: boolean }
): string[] => {
  const ids = new Set<string>();
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const item = row as Record<string, unknown>;
    if (options?.issueOnly) {
      const issued = toNumber(item.issued_qty ?? item.txt_issued_qty);
      const isNew =
        String(item._action || item.act || "").toLowerCase() === "insert" ||
        String(item.act || "").toLowerCase() === "add";
      if (issued <= 0 && !isNew) continue;
    }
    for (const key of ["codeid", "drp_code", "drp_product", "product_id"]) {
      const val = String(item[key] ?? "").trim();
      if (val) ids.add(val);
    }
  }
  return Array.from(ids);
};

export const extractCodeidsFromPartsRequest = (formData: Record<string, any>): string[] => {
  const ids = new Set<string>();
  const arrays = [
    formData?.subform?.flatMap((sf: any) => sf?.data || []) || [],
    formData?.Parts_requested_spareparts,
    formData?.sub_Checklist,
    formData?.parts_request_details,
  ];
  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue;
    collectCodeidsFromRows(arr).forEach((id) => ids.add(id));
  }
  return Array.from(ids);
};

export const extractCodeidsFromPartsIssue = (formData: Record<string, any>): string[] => {
  const ids = new Set<string>();
  const arrays = [
    formData?.service_spare_parts_issue,
    formData?.subform?.flatMap((sf: any) => sf?.data || []) || [],
  ];
  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue;
    collectCodeidsFromRows(arr, { issueOnly: true }).forEach((id) => ids.add(id));
  }
  return Array.from(ids);
};

export const extractCodeidsFromPartsPayload = (
  payload: Record<string, any>,
  context: "parts_request" | "parts_issue"
): string[] => {
  const subforms = payload?.data?.subform || payload?.subform || [];
  const rows = Array.isArray(subforms)
    ? subforms.flatMap((sf: any) => (Array.isArray(sf?.data) ? sf.data : []))
    : [];
  return collectCodeidsFromRows(rows, {
    issueOnly: context === "parts_issue",
  });
};

export async function fetchPurchasedItemsOverlap(params: {
  parentId: string;
  parentType: "jobcard" | "service_details";
  context: "parts_request" | "parts_issue";
  codeids?: string[];
}): Promise<PurchasedOverlapValidation> {
  const response = await apiService.post<PurchasedOverlapValidation & { success?: boolean }>(
    "/pabws/module/parts_overlap/validate_purchased_items",
    {
      parent_id: params.parentId,
      parent_type: params.parentType,
      codeids: params.codeids?.length ? params.codeids : undefined,
      context: params.context,
    },
    {
      includeAuthToken: true,
      authHeaderType: "Bearer",
      errorHandling: "throw",
    }
  );

  return {
    has_overlap: Boolean(response?.has_overlap),
    service_details_id: response?.service_details_id,
    jobcard_no: response?.jobcard_no,
    items: Array.isArray(response?.items) ? response.items : [],
    message: response?.message,
  };
}

export const formatPurchasedOverlapBullets = (
  items: PurchasedOverlapItem[],
  jobcardNo?: string
): string => {
  const header = jobcardNo
    ? `Job card ${jobcardNo} already has purchased items:\n`
    : "This job card already has purchased items:\n";
  const lines = items.map((item) => {
    const parts = [
      item.item_name || "Item",
      item.codeid ? `(Code ${item.codeid})` : "",
      item.purchased_qty != null ? `Qty ${item.purchased_qty}` : "",
      item.purchase_no ? item.purchase_no : "",
      item.purchase_date ? item.purchase_date : "",
      item.source ? `(${item.source})` : "",
    ].filter(Boolean);
    return `• ${parts.join(" — ")}`;
  });
  return `${header}\n${lines.join("\n")}`;
};

export const formatPurchasedOverlapConfirmMessage = (
  items: PurchasedOverlapItem[],
  context: "parts_request" | "parts_issue"
): string => {
  const actionLabel = context === "parts_request" ? "request" : "issue";
  return (
    `The following items in your ${actionLabel} already exist from purchase on this job card:\n\n` +
    `${formatPurchasedOverlapBullets(items)}\n\n` +
    `Proceeding may create duplicate ${actionLabel} records and affect stock.\n\n` +
    `Do you want to continue?`
  );
};

export const isPartsRequestJobcardLookup = (
  lookupName?: string | null
): boolean => String(lookupName || "").trim() === "parts_request_jobcard";

export const isPartsIssueJobcardLookup = (
  lookupName?: string | null
): boolean => {
  const name = String(lookupName || "").trim();
  return name === "parts_issue_jobcard" || name === "parts_issue_jobcard_purchase";
};

export async function triggerPurchasedItemsInfoForPartsRequest(
  dispatch: AppDispatch,
  parentId: string,
  cacheRef: { current: string }
): Promise<void> {
  const pid = String(parentId || "").trim();
  if (!pid) return;
  const cacheKey = `req:${pid}`;
  if (cacheRef.current === cacheKey) return;
  cacheRef.current = cacheKey;

  try {
    const result = await fetchPurchasedItemsOverlap({
      parentId: pid,
      parentType: "jobcard",
      context: "parts_request",
    });
    if (result.items.length > 0) {
      await showPurchasedItemsInfoAlert(dispatch, result.items, result.jobcard_no);
    }
  } catch {
    cacheRef.current = "";
  }
}

export async function triggerPurchasedItemsInfoForPartsIssue(
  dispatch: AppDispatch,
  parentId: string,
  cacheRef: { current: string }
): Promise<void> {
  const pid = String(parentId || "").trim();
  if (!pid) return;
  const cacheKey = `issue:${pid}`;
  if (cacheRef.current === cacheKey) return;
  cacheRef.current = cacheKey;

  try {
    const result = await fetchPurchasedItemsOverlap({
      parentId: pid,
      parentType: "service_details",
      context: "parts_issue",
    });
    if (result.items.length > 0) {
      await showPurchasedItemsInfoAlert(dispatch, result.items, result.jobcard_no);
    }
  } catch {
    cacheRef.current = "";
  }
}

export async function showPurchasedItemsInfoAlert(
  dispatch: AppDispatch,
  items: PurchasedOverlapItem[],
  jobcardNo?: string
): Promise<void> {
  if (!items.length) return;
  const message =
    `${formatPurchasedOverlapBullets(items, jobcardNo)}\n\n` +
    "Further request or issue of the same items may create duplicate lines.";
  await showAlertDialog(dispatch, message, PURCHASED_ITEMS_INFO_TITLE);
}

export async function confirmPurchasedOverlapProceed(
  dispatch: AppDispatch,
  items: PurchasedOverlapItem[],
  context: "parts_request" | "parts_issue"
): Promise<boolean> {
  if (!items.length) return true;
  return showConfirmDialog(
    dispatch,
    formatPurchasedOverlapConfirmMessage(items, context),
    PURCHASED_OVERLAP_CONFIRM_TITLE
  );
}

export async function validatePurchasedOverlapForSave(params: {
  parentId: string;
  parentType: "jobcard" | "service_details";
  context: "parts_request" | "parts_issue";
  codeids: string[];
}): Promise<PurchasedOverlapValidation> {
  if (!params.parentId?.trim()) {
    return { has_overlap: false, items: [] };
  }
  if (params.context === "parts_issue" && params.codeids.length === 0) {
    return { has_overlap: false, items: [] };
  }
  return fetchPurchasedItemsOverlap(params);
}
